
import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { analytics } from "@/services/analytics/analyticsService";

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
  initialValue?: string;
  autoFocus?: boolean;
  isSearching?: boolean;
  disabled?: boolean;
  resultsCount?: number;
  triggerMode?: 'debounced' | 'submit'; // New prop to control when search triggers
}

const SearchBar = ({ 
  onSearch, 
  placeholder = "Search...",
  className = "",
  initialValue = "",
  autoFocus = false,
  isSearching = false,
  disabled = false,
  resultsCount,
  triggerMode = 'debounced' // Default to existing behavior
}: SearchBarProps) => {
  const [query, setQuery] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<number | null>(null);
  const lastSearched = useRef<string>("");

  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    // Only trigger debounced search if in debounced mode
    if (triggerMode === 'debounced') {
      // Clear existing timeout
      if (searchTimeout.current !== null) {
        window.clearTimeout(searchTimeout.current);
      }
      
      // Set a new timeout to avoid tracking intermediate keystrokes
      searchTimeout.current = window.setTimeout(() => {
        onSearch(newQuery);
        
        // Only track if query has changed and is not empty
        if (newQuery && newQuery !== lastSearched.current) {
          lastSearched.current = newQuery;
          
          // Track search immediately, but results count might come later
          if (resultsCount !== undefined) {
            analytics.trackSearch(newQuery, resultsCount);
          }
        }
      }, 300);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    onSearch(query);
    
    // Track search for submit mode
    if (triggerMode === 'submit' && query && query !== lastSearched.current) {
      lastSearched.current = query;
      analytics.trackSearch(query, resultsCount || 0);
    }
  };

  // Track results when they come in (if not tracked in change handler)
  useEffect(() => {
    if (query && resultsCount !== undefined && !isSearching && lastSearched.current === query && triggerMode === 'debounced') {
      analytics.trackSearch(query, resultsCount);
    }
  }, [resultsCount, isSearching, query, triggerMode]);

  const clearSearch = () => {
    setQuery("");
    lastSearched.current = "";
    onSearch("");
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={`relative flex w-full items-center ${className}`}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
        {isSearching ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4" />
        )}
      </div>
      <Input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full pl-10 pr-10"
        autoFocus={autoFocus}
        disabled={disabled}
      />
      {query && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0" 
            onClick={clearSearch}
            disabled={disabled}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
